import { ConfigField, ConfigSlideId } from '@/eventConfig/types.ts';
import { ChangeEventHandler, FC, KeyboardEventHandler, MouseEventHandler } from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { decorate } from '@/form/fields/_pipeline/decorate.ts';
import { normalize } from '@/form/fields/_pipeline/normalize.ts';
import { FieldValue, FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { isEducationEmail } from '@/form/fields/EmailInput/isEducationEmail.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { useFieldValidation } from '../_pipeline/useValidateField';
import { useRevalidateField } from '@/util/useRevalidateField';

interface EmailInputProps {
  field: ConfigField;
  onUIEvent?: (e: any, target: any) => void;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const EmailInput: FC<EmailInputProps> = ({
  field,
  onUIEvent,
  slideId,
  onChange,
  getFieldStatus,
  getFieldValue,
}) => {
  const fieldStatus = getFieldStatus(field.name);
  const { showLayer } = useLayer();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { revalidateFields } = useRevalidateField(validateField);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required');
  const placeholder = translate(field.placeholder);

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = (e) => {
    if (field.click && onUIEvent) {
      onUIEvent(e, field.click);
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const raw = e.target.value;
    const normalized = normalize(field.datatype, raw);
    const value = { raw: raw, normalized };

    const { hint, isValid } = validateField(field.validator, value);

    onChange(field.name, { value, hint, isValid, slideId });
  };

  const handleEducationError = async (fieldValue: FieldValue) => {
    if (!field?.education) {
      return;
    }

    const isCritical = field.education.isCritical;
    const hintLayer = field.education.layer;

    const isEdu = await isEducationEmail(fieldValue);
    if (isEdu) {
      showLayer({ ref: `@${hintLayer}` });

      if (isCritical) {
        onChange(field.name, { value: fieldValue, isValid: false, slideId });
      }
    }
  };

  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!field.decorator) {
      return;
    }
    const value = e.currentTarget.value;
    if (['Enter', 'Backspace', 'Delete'].indexOf(e.key) < 0) {
      const decorated = decorate(field.decorator, value);
      const normalized = normalize(field.datatype, decorated);
      const fieldValue = { raw: decorated, normalized };
      const { hint, isValid } = validateField(field.validator, fieldValue);

      onChange(field.name, { value: fieldValue, hint, isValid, slideId });

      void handleEducationError(fieldValue);
    }

    if (field.revalidate) revalidateFields(field.revalidate);
  };

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
  ];

  const hint = fromTemplate(fieldHint || field.hint || '');

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <label htmlFor={htmlId} className={isRequired ? 'required' : ''} onClick={handleLabelClick}>
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>
        <input
          autoComplete={field.autocomplete ? field.autocomplete : 'on'}
          type="text"
          value={value}
          name={field.name}
          placeholder={placeholder}
          id={htmlId}
          maxLength={100}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
        />
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
